import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Eye, CheckCircle, Clock, FileCheck, AlertTriangle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '@/services/api';
import { toast } from 'sonner';

const StatusBadge = ({ statut }) => {
  const config = {
    'Délivrée': { className: 'bg-green-100 text-green-800', icon: CheckCircle },
    'En signature': { className: 'bg-blue-100 text-blue-800', icon: Clock },
    'À signer': { className: 'bg-blue-100 text-blue-800', icon: Clock },
    'À valider HSE': { className: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
    'Brouillon': { className: 'bg-gray-100 text-gray-800', icon: FileText }
  };
  const { className, icon: Icon } = config[statut] || { className: 'bg-gray-100 text-gray-800' };
  return (
    <Badge className={className}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {statut}
    </Badge>
  );
};

export default function Demandes() {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadDemandes();
  }, []);

  const loadDemandes = async () => {
    try {
      setLoading(true);
      const data = await api.getDemandes();
      // Filter only non-delivered demands (workflow items)
      const workflowDemandes = data.filter(d => d.statut !== 'Délivrée' && d.statut !== 'Révoquée' && d.statut !== 'Échue');
      setDemandes(workflowDemandes);
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const filteredDemandes = demandes.filter(demande => {
    if (activeTab === 'all') return true;
    if (activeTab === 'hse') return demande.statut === 'À valider HSE';
    if (activeTab === 'signature') return demande.statut === 'En signature' || demande.statut === 'À signer';
    if (activeTab === 'brouillon') return demande.statut === 'Brouillon';
    return true;
  });

  const countByStatus = {
    all: demandes.length,
    hse: demandes.filter(d => d.statut === 'À valider HSE').length,
    signature: demandes.filter(d => d.statut === 'En signature' || d.statut === 'À signer').length,
    brouillon: demandes.filter(d => d.statut === 'Brouillon').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-7 h-7 text-blue-600" />
            Demandes d'autorisation
          </h1>
          <p className="text-gray-500 mt-1">Gestion du workflow de validation</p>
        </div>
        <Link to="/nouvelle-autorisation">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Nouvelle demande
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total en cours</p>
                <p className="text-2xl font-bold">{countByStatus.all}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">À valider HSE</p>
                <p className="text-2xl font-bold">{countByStatus.hse}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-100">
                <FileCheck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En signature</p>
                <p className="text-2xl font-bold">{countByStatus.signature}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-gray-100">
                <Clock className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Brouillons</p>
                <p className="text-2xl font-bold">{countByStatus.brouillon}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Toutes ({countByStatus.all})</TabsTrigger>
          <TabsTrigger value="hse">À valider HSE ({countByStatus.hse})</TabsTrigger>
          <TabsTrigger value="signature">En signature ({countByStatus.signature})</TabsTrigger>
          <TabsTrigger value="brouillon">Brouillons ({countByStatus.brouillon})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredDemandes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucune demande en cours</p>
                  <Link to="/nouvelle-autorisation">
                    <Button variant="link">Créer une demande</Button>
                  </Link>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Catégories</TableHead>
                      <TableHead>Date création</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDemandes.map((demande) => (
                      <TableRow key={demande.id}>
                        <TableCell className="font-mono text-sm">{demande.numero}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {demande.collaborateur?.prenom} {demande.collaborateur?.nom}
                            </p>
                            <p className="text-sm text-gray-500">{demande.collaborateur?.secteur}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {demande.categories?.map((cat, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {demande.date_creation ? new Date(demande.date_creation).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                        <TableCell><StatusBadge statut={demande.statut} /></TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Link to={`/demandes/${demande.id}`}>
                              <Button variant="ghost" size="sm" className="gap-1">
                                <Eye className="h-4 w-4" />
                                Voir
                              </Button>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
